import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import Navbar from '../../components/Navbar';
import SidebarSeccion from '../../components/SidebarSeccion';
import { useAuth, API_URL } from '../../context/AuthContext';
import { getTema } from '../../theme';
import { XMarkIcon } from '@heroicons/react/24/outline';

const CATEGORIAS = ['Writing', 'Listening', 'Reading', 'Speaking'];

const preguntaBase = () => ({
  texto: '', tipo_pregunta: 'multiple',
  opciones: ['', '', '', ''],
  respuesta_correcta: '',
});

export default function CrearEvaluacion({ tipo, nivel, subtipo = 'examen' }) {
  const { usuario } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const tema = getTema(tipo);
  const esProfesor = ['profesor', 'admin'].includes(usuario?.rol);
  const esActividad = subtipo === 'actividad';
  const etiqueta = esActividad ? 'Actividad' : 'Examen';

  // Si venimos desde Plantillas, pre-rellenamos con los datos de la plantilla
  const plantillaInicial = location.state?.plantilla;

  const [titulo, setTitulo] = useState(plantillaInicial?.titulo || '');
  const [descripcion, setDescripcion] = useState(plantillaInicial?.descripcion || '');
  const [categoria, setCategoria] = useState(plantillaInicial?.categoria || 'Writing');
  const [duracion, setDuracion] = useState(plantillaInicial?.duracion_minutos || '');
  const [fecha, setFecha] = useState('');
  const [preguntas, setPreguntas] = useState(plantillaInicial?.preguntas?.length > 0 ? plantillaInicial.preguntas : [preguntaBase()]);
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState('');
  const [plantillaUsada, setPlantillaUsada] = useState(plantillaInicial || null);

  // Campos específicos por categoría
  const [subtipoEval, setSubtipoEval] = useState(plantillaInicial?.subtipo_eval || 'test');
  const [linkAudio, setLinkAudio] = useState(plantillaInicial?.link_audio || '');
  const [textoLectura, setTextoLectura] = useState(plantillaInicial?.texto_lectura || '');

  // Selector de plantillas
  const [plantillas, setPlantillas] = useState([]);
  const [mostrarSelectorPlantillas, setMostrarSelectorPlantillas] = useState(false);
  const [cargandoPlantillas, setCargandoPlantillas] = useState(false);

  useEffect(() => {
    if (!esProfesor) return;
    axios.get(`${API_URL}/plantillas`).then(r => setPlantillas(r.data)).catch(() => {});
  }, []);

  const aplicarPlantilla = async (p) => {
    setCargandoPlantillas(true);
    try {
      const res = await axios.get(`${API_URL}/plantillas/${p.id}`);
      const pl = res.data;
      setTitulo(pl.titulo || '');
      setDescripcion(pl.descripcion || '');
      setCategoria(pl.categoria || 'Writing');
      setDuracion(pl.duracion_minutos || '');
      setSubtipoEval(pl.subtipo_eval || 'test');
      setLinkAudio(pl.link_audio || '');
      setTextoLectura(pl.texto_lectura || '');
      setPreguntas(pl.preguntas?.length > 0 ? pl.preguntas : [preguntaBase()]);
      setPlantillaUsada(pl);
      setMostrarSelectorPlantillas(false);
    } catch { }
    setCargandoPlantillas(false);
  };

  const esListening = categoria === 'Listening';
  const esReading = categoria === 'Reading';
  const esSpeaking = categoria === 'Speaking';
  const esRedaccion = categoria === 'Writing' && subtipoEval === 'redaccion';
  const soloMultiple = esListening || esReading;

  // Al cambiar categoría: resetear campos de categoría y normalizar preguntas
  useEffect(() => {
    if (soloMultiple) {
      setPreguntas(prev => prev.map(p => ({
        ...p,
        tipo_pregunta: 'multiple',
        opciones: p.tipo_pregunta !== 'multiple' ? ['', '', '', ''] : p.opciones,
        respuesta_correcta: p.tipo_pregunta !== 'multiple' ? '' : p.respuesta_correcta,
      })));
    }
  }, [categoria]);

  if (!esProfesor) return <div style={{ color: tema.text, padding: '32px' }}>Sin acceso</div>;

  if (esSpeaking) {
    return (
      <div style={{ minHeight: '100vh', background: tema.bg }}>
        <Navbar tema={tema} titulo={tipo === 'militar' ? 'Programa Militar' : 'Inglés Civil'} tipo={tipo} nivel={nivel} />
        <div className="page-layout">
          <SidebarSeccion tema={tema} tipo={tipo} nivel={nivel} />
          <main className="page-content">
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '50vh', textAlign: 'center' }}>
              <div style={{ fontSize: '48px', marginBottom: '16px' }}>🎙️</div>
              <h2 style={{ color: tema.text, marginBottom: '8px' }}>Speaking se gestiona desde Actividades</h2>
              <p style={{ color: tema.muted, marginBottom: '24px' }}>Las sesiones de Speaking se crean directamente en la pestaña Speaking de la página de Actividades.</p>
              <button onClick={() => navigate(`/${tipo}/${nivel}/actividades`)}
                style={{ background: tema.primary, border: 'none', color: '#fff', padding: '10px 24px', borderRadius: '50px', fontSize: '14px', cursor: 'pointer' }}>
                Ir a Actividades
              </button>
            </div>
          </main>
        </div>
      </div>
    );
  }

  const agregarPregunta = () => setPreguntas([...preguntas, { ...preguntaBase(), tipo_pregunta: soloMultiple ? 'multiple' : 'multiple' }]);
  const eliminarPregunta = (idx) => setPreguntas(preguntas.filter((_, i) => i !== idx));

  const actualizarPregunta = (idx, campo, valor) => {
    const nuevas = [...preguntas];
    nuevas[idx] = { ...nuevas[idx], [campo]: valor };
    if (campo === 'tipo_pregunta' && valor === 'verdadero_falso') {
      nuevas[idx].opciones = ['Verdadero', 'Falso'];
      nuevas[idx].respuesta_correcta = '';
    }
    if (campo === 'tipo_pregunta' && valor === 'multiple') {
      nuevas[idx].opciones = ['', '', '', ''];
      nuevas[idx].respuesta_correcta = '';
    }
    if (campo === 'tipo_pregunta' && valor === 'rellenar_huecos') {
      nuevas[idx].opciones = [];
      nuevas[idx].respuesta_correcta = '[]';
    }
    if (campo === 'texto' && nuevas[idx].tipo_pregunta === 'rellenar_huecos') {
      const numHuecos = (valor.match(/___/g) || []).length;
      const opsActuales = nuevas[idx].opciones || [];
      const ops = Array.from({ length: numHuecos }, (_, i) => opsActuales[i] || '');
      nuevas[idx].opciones = ops;
      nuevas[idx].respuesta_correcta = JSON.stringify(ops);
    }
    setPreguntas(nuevas);
  };

  const actualizarOpcion = (pIdx, oIdx, valor) => {
    const nuevas = [...preguntas];
    const opciones = [...nuevas[pIdx].opciones];
    opciones[oIdx] = valor;
    nuevas[pIdx] = { ...nuevas[pIdx], opciones };
    setPreguntas(nuevas);
  };

  const actualizarHueco = (pIdx, hIdx, valor) => {
    const nuevas = [...preguntas];
    const ops = [...(nuevas[pIdx].opciones || [])];
    ops[hIdx] = valor;
    nuevas[pIdx] = { ...nuevas[pIdx], opciones: ops, respuesta_correcta: JSON.stringify(ops) };
    setPreguntas(nuevas);
  };

  const handleGuardar = async (e) => {
    e.preventDefault();
    if (!titulo) return setError('El título es obligatorio');
    if (esListening && !linkAudio.trim()) return setError('El enlace al audio es obligatorio para Listening');
    if (esReading && !textoLectura.trim()) return setError('El texto de lectura es obligatorio para Reading');
    if (!esRedaccion) {
      for (const p of preguntas) {
        if (!p.texto) return setError('Todas las preguntas deben tener texto');
        if (p.tipo_pregunta === 'rellenar_huecos') {
          const huecos = (p.texto.match(/___/g) || []).length;
          if (huecos === 0) return setError('Las preguntas de rellenar huecos deben contener al menos un ___');
          if (p.opciones.length !== huecos || p.opciones.some(o => !o.trim()))
            return setError('Debes escribir la respuesta correcta para cada hueco');
        } else {
          if (!p.respuesta_correcta) return setError('Todas las preguntas deben tener una respuesta correcta');
        }
      }
    }
    setGuardando(true);
    try {
      await axios.post(`${API_URL}/evaluaciones`, {
        titulo, descripcion, tipo, nivel, categoria, subtipo,
        subtipo_eval: subtipoEval,
        duracion_minutos: duracion ? parseInt(duracion) : null,
        preguntas: esRedaccion ? [] : preguntas,
        link_audio: esListening ? linkAudio.trim() : null,
        texto_lectura: esReading ? textoLectura.trim() : null,
        fecha: fecha || null,
      });
      navigate(`/${tipo}/${nivel}/actividades`);
    } catch (e) {
      setError(e.response?.data?.error || 'Error al guardar');
    } finally { setGuardando(false); }
  };

  const inputStyle = { width: '100%', padding: '10px 14px', borderRadius: '8px', background: tema.bg, border: `1px solid ${tema.border}`, color: tema.text, fontSize: '14px' };
  const labelStyle = { color: tema.muted, fontSize: '12px', display: 'block', marginBottom: '6px' };

  const iconoCat = { Writing: '✍️', Listening: '🎧', Reading: '📖', Speaking: '🎙️' };

  return (
    <div style={{ minHeight: '100vh', background: tema.bg }}>
      <Navbar tema={tema} titulo={tipo === 'militar' ? 'Programa Militar' : 'Inglés Civil'} tipo={tipo} nivel={nivel} />
      <div className="page-layout">
        <SidebarSeccion tema={tema} tipo={tipo} nivel={nivel} />
        <main className="page-content">
          <div className="fade-in" style={{ maxWidth: '780px' }}>
            <div style={{ marginBottom: '28px' }}>
              <h2 style={{ color: tema.text, fontSize: '26px', marginBottom: '4px' }}>
                {esActividad ? '📋' : '🧪'} Crear {etiqueta}
              </h2>
              <p style={{ color: tema.muted, fontSize: '14px' }}>Diseña un test con autocorrección para {nivel}</p>
            </div>

            {/* Selector de plantillas */}
            <div style={{ marginBottom: '20px' }}>
              {plantillaUsada ? (
                <div style={{ background: `${tema.accent}12`, border: `1px solid ${tema.accent}33`, borderRadius: '10px', padding: '12px 16px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <span style={{ fontSize: '18px' }}>📋</span>
                    <p style={{ color: tema.accent, fontSize: '13px', margin: 0, fontWeight: 600 }}>
                      Plantilla cargada: <em style={{ fontWeight: 400 }}>{plantillaUsada.titulo}</em>
                    </p>
                  </div>
                  <button type="button" onClick={() => setMostrarSelectorPlantillas(true)}
                    style={{ background: 'none', border: `1px solid ${tema.accent}55`, color: tema.accent, padding: '6px 14px', borderRadius: '20px', fontSize: '12px', cursor: 'pointer' }}>
                    Cambiar
                  </button>
                </div>
              ) : plantillas.length > 0 ? (
                <button type="button" onClick={() => setMostrarSelectorPlantillas(true)}
                  style={{ width: '100%', background: `${tema.accent}08`, border: `2px dashed ${tema.accent}44`, color: tema.accent, padding: '12px', borderRadius: '10px', fontSize: '14px', fontWeight: 600, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}>
                  📋 Cargar desde plantilla
                </button>
              ) : null}
            </div>

            {/* Modal selector de plantillas */}
            {mostrarSelectorPlantillas && (
              <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px' }}>
                <div style={{ background: tema.bgCard, borderRadius: '16px', padding: '28px', width: '100%', maxWidth: '520px', maxHeight: '80vh', display: 'flex', flexDirection: 'column' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                    <h3 style={{ color: tema.text, margin: 0, fontSize: '18px' }}>📋 Elegir plantilla</h3>
                    <button type="button" onClick={() => setMostrarSelectorPlantillas(false)}
                      style={{ background: 'none', border: 'none', color: tema.muted, cursor: 'pointer', fontSize: '20px' }}>✕</button>
                  </div>
                  <div style={{ overflowY: 'auto', flex: 1, display: 'flex', flexDirection: 'column', gap: '10px' }}>
                    {plantillas.length === 0 ? (
                      <p style={{ color: tema.muted, textAlign: 'center', padding: '32px 0' }}>No hay plantillas guardadas</p>
                    ) : plantillas.map(p => (
                      <button key={p.id} type="button" onClick={() => aplicarPlantilla(p)}
                        disabled={cargandoPlantillas}
                        style={{ background: tema.bg, border: `1px solid ${tema.border}`, borderRadius: '10px', padding: '14px 16px', cursor: 'pointer', textAlign: 'left', transition: 'border-color 0.15s' }}
                        onMouseEnter={e => e.currentTarget.style.borderColor = tema.accent}
                        onMouseLeave={e => e.currentTarget.style.borderColor = tema.border}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                          <div>
                            <p style={{ color: tema.text, fontWeight: 600, fontSize: '14px', margin: '0 0 4px' }}>{p.titulo}</p>
                            {p.descripcion && <p style={{ color: tema.muted, fontSize: '12px', margin: 0 }}>{p.descripcion}</p>}
                          </div>
                          <div style={{ display: 'flex', gap: '6px', flexShrink: 0, marginLeft: '12px' }}>
                            {p.categoria && <span style={{ background: `${tema.accent}18`, color: tema.accent, fontSize: '11px', padding: '2px 8px', borderRadius: '10px' }}>{p.categoria}</span>}
                            {p.duracion_minutos && <span style={{ background: tema.bg, color: tema.muted, fontSize: '11px', padding: '2px 8px', borderRadius: '10px', border: `1px solid ${tema.border}` }}>{p.duracion_minutos}min</span>}
                          </div>
                        </div>
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {error && (
              <div style={{ background: 'rgba(231,76,60,0.1)', border: '1px solid rgba(231,76,60,0.3)', borderRadius: '8px', padding: '12px', color: '#e74c3c', marginBottom: '20px', fontSize: '14px' }}>
                {error}
              </div>
            )}

            <form onSubmit={handleGuardar}>
              {/* Datos generales */}
              <div style={{ background: tema.bgCard, border: `1px solid ${tema.border}`, borderRadius: '12px', padding: '24px', marginBottom: '24px' }}>
                <div className="form-grid-2">
                  <div>
                    <label style={labelStyle}>Título *</label>
                    <input style={inputStyle} value={titulo} onChange={e => setTitulo(e.target.value)} required placeholder="Ej: Listening Unit 3" />
                  </div>
                  <div>
                    <label style={labelStyle}>Categoría *</label>
                    <select style={inputStyle} value={categoria} onChange={e => setCategoria(e.target.value)}>
                      {CATEGORIAS.map(c => (
                        <option key={c} value={c}>{iconoCat[c]} {c}</option>
                      ))}
                    </select>
                  </div>
                  {!esRedaccion && (
                    <div>
                      <label style={labelStyle}>Tiempo límite (opcional)</label>
                      <select style={inputStyle} value={duracion} onChange={e => setDuracion(e.target.value)}>
                        <option value="">Sin límite</option>
                        {[5,10,15,20,30,45,60,90].map(m => <option key={m} value={m}>{m} minutos</option>)}
                      </select>
                    </div>
                  )}
                  <div>
                    <label style={labelStyle}>📅 Fecha {esActividad ? 'de entrega' : 'del examen'} (opcional)</label>
                    <input type="date" style={inputStyle} value={fecha} onChange={e => setFecha(e.target.value)} />
                  </div>
                </div>
                <div>
                  <label style={labelStyle}>{esRedaccion ? 'Tema / instrucciones para el alumno *' : 'Descripción (opcional)'}</label>
                  <textarea
                    style={{ ...inputStyle, height: esRedaccion ? '100px' : '60px', resize: 'vertical' }}
                    value={descripcion}
                    onChange={e => setDescripcion(e.target.value)}
                    placeholder={esRedaccion ? 'Ej: Escribe un correo formal presentándote a tu nuevo jefe...' : 'Instrucciones o descripción del test'}
                    required={esRedaccion}
                  />
                </div>

                {/* Writing: selector Test / Redacción */}
                {categoria === 'Writing' && (
                  <div style={{ marginTop: '14px' }}>
                    <label style={labelStyle}>Tipo de prueba</label>
                    <div style={{ display: 'flex', gap: '10px' }}>
                      {[{ val: 'test', label: '📝 Test (autocorrección)' }, { val: 'redaccion', label: '✍️ Redacción (corrección manual)' }].map(op => (
                        <button key={op.val} type="button"
                          onClick={() => setSubtipoEval(op.val)}
                          style={{
                            padding: '8px 18px', borderRadius: '20px', fontSize: '13px', fontWeight: 600, cursor: 'pointer',
                            border: subtipoEval === op.val ? `2px solid ${tema.accent}` : `2px solid ${tema.border}`,
                            background: subtipoEval === op.val ? `${tema.accent}18` : tema.bg,
                            color: subtipoEval === op.val ? tema.accent : tema.muted,
                          }}>
                          {op.label}
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {/* Listening: URL del audio */}
                {esListening && (
                  <div style={{ marginTop: '14px' }}>
                    <label style={labelStyle}>Enlace al audio (YouTube) *</label>
                    <input style={inputStyle} value={linkAudio} onChange={e => setLinkAudio(e.target.value)}
                      placeholder="https://www.youtube.com/watch?v=..." required />
                    <p style={{ color: tema.muted, fontSize: '12px', marginTop: '6px' }}>
                      El vídeo/audio se mostrará al alumno mientras responde las preguntas
                    </p>
                  </div>
                )}

                {/* Reading: texto de lectura */}
                {esReading && (
                  <div style={{ marginTop: '14px' }}>
                    <label style={labelStyle}>Texto de lectura *</label>
                    <textarea style={{ ...inputStyle, height: '160px', resize: 'vertical' }}
                      value={textoLectura} onChange={e => setTextoLectura(e.target.value)}
                      placeholder="Escribe aquí el texto que el alumno deberá leer antes de responder las preguntas..."
                      required />
                  </div>
                )}
              </div>

              {/* Redacción: sin preguntas */}
              {esRedaccion ? (
                <div style={{ background: `${tema.accent}11`, border: `1px solid ${tema.accent}33`, borderRadius: '12px', padding: '20px', marginBottom: '24px', textAlign: 'center' }}>
                  <div style={{ fontSize: '32px', marginBottom: '8px' }}>✍️</div>
                  <p style={{ color: tema.accent, fontWeight: 600, marginBottom: '4px' }}>Redacción de texto libre</p>
                  <p style={{ color: tema.muted, fontSize: '13px' }}>
                    El alumno escribirá su respuesta y quedará pendiente hasta que tú le asignes una nota manualmente.
                  </p>
                </div>
              ) : (
                <>
                  {soloMultiple && (
                    <div style={{ background: `${tema.accent}11`, border: `1px solid ${tema.accent}33`, borderRadius: '10px', padding: '12px 16px', marginBottom: '16px', fontSize: '13px', color: tema.accent }}>
                      {esListening ? '🎧' : '📖'} Las preguntas de {categoria} son solo de opción múltiple (4 opciones)
                    </div>
                  )}

                  {preguntas.map((p, pIdx) => (
                    <div key={pIdx} style={{ background: tema.bgCard, border: `1px solid ${tema.border}`, borderRadius: '12px', padding: '24px', marginBottom: '16px' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '16px' }}>
                        <span style={{ color: tema.accent, fontWeight: 700, fontFamily: 'Inter, sans-serif', fontSize: '15px' }}>
                          Pregunta {pIdx + 1}
                        </span>
                        {preguntas.length > 1 && (
                          <button type="button" onClick={() => eliminarPregunta(pIdx)}
                            style={{ background: 'none', border: 'none', color: '#e74c3c', cursor: 'pointer', fontSize: '13px', display: 'flex', alignItems: 'center', gap: '4px' }}>
                            <XMarkIcon style={{ width: 14, height: 14 }} /> Eliminar
                          </button>
                        )}
                      </div>

                      {!soloMultiple && (
                        <div style={{ marginBottom: '14px' }}>
                          <label style={labelStyle}>Tipo de pregunta</label>
                          <select style={inputStyle} value={p.tipo_pregunta} onChange={e => actualizarPregunta(pIdx, 'tipo_pregunta', e.target.value)}>
                            <option value="multiple">Opción múltiple (4 opciones)</option>
                            <option value="verdadero_falso">Verdadero / Falso</option>
                            <option value="rellenar_huecos">Rellenar huecos con palabras</option>
                          </select>
                        </div>
                      )}

                      <div style={{ marginBottom: '14px' }}>
                        <label style={labelStyle}>Enunciado de la pregunta *</label>
                        {p.tipo_pregunta === 'rellenar_huecos' && (
                          <p style={{ color: tema.accent, fontSize: '12px', marginBottom: '6px' }}>
                            Usa ___ para marcar cada hueco
                          </p>
                        )}
                        <textarea style={{ ...inputStyle, height: '70px', resize: 'vertical' }}
                          value={p.texto} onChange={e => actualizarPregunta(pIdx, 'texto', e.target.value)} required />
                      </div>

                      {p.tipo_pregunta === 'rellenar_huecos' ? (
                        <div style={{ marginBottom: '14px' }}>
                          {(p.texto.match(/___/g) || []).length === 0 ? (
                            <p style={{ color: tema.muted, fontSize: '13px', fontStyle: 'italic' }}>
                              Escribe el enunciado con ___ para que aparezcan los campos de respuesta
                            </p>
                          ) : (
                            <>
                              <label style={{ ...labelStyle, marginBottom: '10px' }}>Respuesta correcta para cada hueco</label>
                              {Array.from({ length: (p.texto.match(/___/g) || []).length }, (_, hIdx) => (
                                <div key={hIdx} style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '8px' }}>
                                  <span style={{ color: tema.muted, fontSize: '13px', minWidth: '68px' }}>Hueco {hIdx + 1}:</span>
                                  <input style={{ ...inputStyle, flex: 1 }}
                                    value={p.opciones[hIdx] || ''}
                                    onChange={e => actualizarHueco(pIdx, hIdx, e.target.value)}
                                    placeholder={`Palabra correcta ${hIdx + 1}`} />
                                </div>
                              ))}
                            </>
                          )}
                        </div>
                      ) : (
                        <div style={{ marginBottom: '14px' }}>
                          <label style={{ ...labelStyle, marginBottom: '10px' }}>Opciones — selecciona la correcta (●)</label>
                          {p.opciones.map((op, oIdx) => (
                            <div key={oIdx} style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '8px' }}>
                              <input type="radio" name={`correcta-${pIdx}`}
                                checked={p.respuesta_correcta === op && op !== ''}
                                onChange={() => actualizarPregunta(pIdx, 'respuesta_correcta', op)}
                                style={{ accentColor: tema.accent, width: '16px', height: '16px' }} />
                              {p.tipo_pregunta === 'verdadero_falso' ? (
                                <span style={{ color: tema.text, fontSize: '14px', padding: '8px 14px', background: tema.bg, border: `1px solid ${tema.border}`, borderRadius: '6px', flex: 1 }}>{op}</span>
                              ) : (
                                <input style={{ ...inputStyle, flex: 1 }} value={op}
                                  onChange={e => {
                                    actualizarOpcion(pIdx, oIdx, e.target.value);
                                    if (p.respuesta_correcta === p.opciones[oIdx])
                                      actualizarPregunta(pIdx, 'respuesta_correcta', e.target.value);
                                  }}
                                  placeholder={`Opción ${oIdx + 1}`} />
                              )}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}

                  <button type="button" onClick={agregarPregunta}
                    style={{ width: '100%', background: `${tema.accent}11`, border: `2px dashed ${tema.accent}44`, color: tema.accent, padding: '14px', borderRadius: '10px', fontSize: '14px', fontWeight: 600, cursor: 'pointer', marginBottom: '24px' }}>
                    + Añadir pregunta
                  </button>
                </>
              )}

              <div style={{ display: 'flex', gap: '12px' }}>
                <button type="submit" disabled={guardando}
                  style={{ background: tema.primary, border: 'none', color: '#fff', padding: '12px 28px', borderRadius: '50px', fontSize: '15px', fontWeight: 600, cursor: 'pointer', boxShadow: '0 4px 14px rgba(139,111,212,0.4)' }}>
                  {guardando ? 'Guardando...' : `💾 Guardar ${etiqueta.toLowerCase()}`}
                </button>
                <button type="button" onClick={() => navigate(`/${tipo}/${nivel}/actividades`)}
                  style={{ background: 'none', border: `1px solid ${tema.border}`, color: tema.muted, padding: '12px 20px', borderRadius: '8px', fontSize: '14px', cursor: 'pointer' }}>
                  Cancelar
                </button>
              </div>
            </form>
          </div>
        </main>
      </div>
    </div>
  );
}
